<template>
  <div
    :style="dashboardContainerType(this.dashboardType)"
    style="height: 100%; max-height: 100% !important"
  >
    <v-dialog v-model="modifyItemsDialog" max-width="450">
      <v-card>
        <v-card-text>
          <template-dashboard-slot-selector
            v-model="itemsToModify"
            updateable
            @updateDefaults="setItemValuesAsDefaults"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-toolbar dense flat>
      <v-btn
        icon
        fab
        @click="$emit('close')"
        v-if="dashboardType == dashboardTypes.SideAndMap?.value"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <div
        v-if="
          dashboardType == dashboardTypes.Full?.value &&
          !publicDashboard &&
          chips.length > 0
        "
      >
        <v-chip @click="() => openDialog(0)">
          <v-icon left> mdi-chevron-down </v-icon>
          {{ $t("templateDashboard.slots.allSlots") }}
        </v-chip>

        <v-chip
          v-for="(item, itemIndex) in chips"
          :key="itemIndex"
          class="ml-1"
          @click="() => openDialog(item.index)"
        >
          <v-icon left> mdi-chevron-down </v-icon>
          {{ item.name }}
        </v-chip>
      </div>
    </v-toolbar>

    <template-dashboard v-if="fetched" :tags="templateDashboardTags" not-form />
    <v-progress-circular v-else />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { templateDashboardMixin } from "../../_helpers/templateDashboardMixin";
import { templateDashboardTypes } from "../../_helpers/templateDashboardHelper.js";
import TemplateDashboard from "./TemplateDashboard.vue";
import TemplateDashboardSlotSelector from "./TemplateDashboardSlotSelector.vue";

export default {
  name: "TemplateDashboardContainer",
  components: { TemplateDashboard, TemplateDashboardSlotSelector },
  mixins: [templateDashboardMixin],
  props: {
    templateDashboardId: {
      type: String,
      default: undefined,
    },

    dashboardId: {
      default: undefined,
    },

    tags: {
      type: Array,
      default: () => [],
    },

    dashboardType: {
      type: Number,
      default: 1,
    },

    value: {
      type: Array,
      default: () => [],
    },

    publicDashboard: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      fetched: false,
      modifyItemsDialog: false,
      modifySelectedIndex: 0,
      allTags: [],
    };
  },

  computed: {
    ...mapState("templateDashboard", {
      storeWidgets: "widgets",
    }),

    widgets: {
      get() {
        return this.storeWidgets;
      },

      set(val) {
        this.setWidgets(val);
      },
    },

    localItems: {
      get() {
        return this.value;
      },

      set(val) {
        this.$emit("input", val);
      },
    },

    chips() {
      return this.localItems
        .filter((x) => x.index != -1)
        .map((x) => {
          return { ...x, name: this.getDeveuiName(x.tagId) };
        });
    },

    dashboardTypes: () => templateDashboardTypes,

    templateDashboardTags() {
      if (this.localItems.length > 0) {
        return (
          this.localItems
            .filter((x) => x.tagId != "" && x.tagId != null)
            ?.map((x) => x.tagId) ?? []
        );
      }
      if (this.tags.length > 0) return this.tags;
      return [];
    },

    itemsToModify: {
      get() {
        if (this.modifySelectedIndex == 0) return this.localItems;
        return this.localItems.filter(
          (x) => x.index == this.modifySelectedIndex
        );
      },
    },
  },

  methods: {
    ...mapActions("templateDashboard", [
      "getDashboardWidgets",
      "setWidgets",
      "saveTemplateDashboardItems",
    ]),
    ...mapActions("tag", ["getTagsWithInformation"]),
    ...mapActions("alert", ["success", "error"]),

    openDialog(index) {
      this.modifySelectedIndex = index;
      this.modifyItemsDialog = true;
    },

    async fetch() {
      this.fetched = false;
      if (
        this.templateDashboardId === undefined ||
        this.templateDashboardId === ""
      ) {
        this.fetched = true;
        return;
      }

      // Get the template dashboard with widgets
      await this.getDashboardWidgets(this.templateDashboardId);
      await this.updateStoreWidgetSlotsKeysAndItems();

      // TODO: Get the meta data for tags that are needed

      this.fetched = true;
    },

    async updateStoreWidgetSlotsKeysAndItems() {
      let allSlotKeys = [];
      for (var item of this.localItems) {
        allSlotKeys = allSlotKeys.concat(item.slotKeys);
      }

      let notCreatedItems = [];
      for (var w of this.widgets) {
        for (var s of w?.slots) {
          let slotkey = allSlotKeys.find((x) => x.slotId == s.slotId);

          // If slotKey is found => Update the store widgets slot to use the key selected in item.
          if (slotkey != undefined) {
            s.key = slotkey?.key ?? s.key;
            slotkey.slotDescription = s.slotDescription;
            continue;
          }

          // If slotKey doesn't exists => Template has been modified since dashboard creation and new temporary items needs to be created.
          //Determine if adding this slot to an existing Items slotKeys OR creating a new Item
          let locItem = this.localItems.find((x) => x.index == s?.index);
          let tempItem = notCreatedItems.find((x) => x.index == s?.index);
          if (locItem == undefined && tempItem == undefined) {
            notCreatedItems.push({
              index: s.index,
              tagId: "",
              templateDashboardId: this.templateDashboardId,
              slotKeys: [
                {
                  key: "",
                  slotId: s.slotId,
                  slotDescription: s?.slotDescription,
                },
              ],
            });
            continue;
          }

          if (locItem != undefined) {
            let key = locItem?.slotKeys.find(
              (x) => x.slotDescription == s?.slotDescription
            )?.key;
            locItem.slotKeys.push({
              key: key ?? "",
              slotId: s.slotId,
              slotDescription: s?.slotDescription,
            });
          }

          if (tempItem != undefined) {
            let key = tempItem?.slotKeys.find(
              (x) => x.slotDescription == s?.slotDescription
            )?.key;
            tempItem.slotKeys.push({
              key: key ?? "",
              slotId: s.slotId,
              slotDescription: s?.slotDescription,
            });
          }
        }
      }

      if (notCreatedItems.length > 0)
        this.localItems = this.localItems.concat(notCreatedItems);
    },

    async setItemValuesAsDefaults() {
      if (this.dashboardId == undefined) {
        this.error(this.$t("common.error"));
        return;
      }

      var result = await this.saveTemplateDashboardItems({
        dashboardId: this.dashboardId,
        payload: this.localItems,
      });

      if (result !== true) {
        this.error(this.$t("common.error"));
        return;
      }
      this.success(this.$t("common.updated"));
      await this.fetch();
    },

    getDeveuiName(deveui) {
      let tag = this.allTags.find((x) => x.deveui == deveui);
      return tag != undefined ? tag.name : deveui;
    },
  },

  async created() {
    this.allTags = await this.getTagsWithInformation();
    await this.fetch();
  },

  watch: {
    async templateDashboardId() {
      // Check for empty or null is made in fetch function
      await this.fetch();
    },

    localItems: {
      handler() {
        this.updateStoreWidgetSlotsKeysAndItems();
      },
      deep: true,
    },
  },
};
</script>
